import { Suspense, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IMaintenance } from "../../../../../types/maintenance.type";
import { useToastContext } from "../../../../../common/components/ui/toast/toast.context";
import {
  TableFilters,
  TableFiltersState,
} from "../../../../../common/components/ui/table/TableFilters";
import { getMaintenanceReports } from "../../../../../common/services/maintenance.api";
import GenericTable from "../../../../../common/components/ui/table/GenericTable";
import Loading from "../../../../../common/components/ui/loading/Loading";
import TableFooter from "../../../../../common/components/ui/table/TableFooter";
import { AuthContext } from "../../../../../common/components/auth/authContext/AuthContext";
import { getCompanySigla } from "@/common/helpers/company.helpers";
import { useCompanyStore } from "@/common/states/store/Company";

const STATUS_LABELS = {
  confirmed: "Confirmado",
  pending: "Pendente",
};

const headers = {
  companyId: "Cidade",
  licensePlate: "Placa",
  partnerCompanyName: "Parceiro",
  status: "Conferência",
};

function TableMaintenanceReports() {
  useContext(AuthContext);
  const navigate = useNavigate();
  const { notifyError } = useToastContext();
  const [page, setPage] = useState<number>(1);
  const [data, setData] = useState({ itemsPerPage: 10, totalItems: 0 });
  const [maintenance, setMaintenance] = useState<IMaintenance[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState<TableFiltersState>({
    search: "",
    status: [],
    filterColumn: "",
  });

  const { selectedCompanies, filterMode } = useCompanyStore();

  useEffect(() => {
    async function loadMaintenance(filters, page) {
      setIsLoading(true);
      const result = await getMaintenanceReports({ ...filters, page });

      setData({
        itemsPerPage: data.itemsPerPage,
        totalItems: result.totalItems,
      });
      if (!result.ok) {
        notifyError(
          (result as { message?: string })?.message || "unkown error"
        );
        return;
      }

      setMaintenance(result.data);
      setIsLoading(false);
    }

    loadMaintenance(filters, page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, page, selectedCompanies, filterMode]);

  const handleFilterChange = (newFilters: Partial<TableFiltersState>) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      ...newFilters,
    }));
  };

  return (
    <Suspense fallback={<Loading />}>
      <div className="table-section">
        <TableFilters
          dashboardHeader="Relatório de Manutenções"
          placeholder="Digite a placa ou o parceiro."
          statusOptions={STATUS_LABELS}
          onNewItemClick={() => {}}
          onSearch={handleFilterChange}
          entity="maintenance"
          tableData={maintenance}
        />

        <GenericTable
          headers={headers}
          data={maintenance}
          setFilters={({ orderBy }) => {
            setFilters((prevFilters) => ({
              ...prevFilters,
              orderBy,
            }));
          }}
          renders={
            {
              companyId: (companyId: string) =>
                getCompanySigla(companyId as string),

              licensePlate: (licensePlate, maintenance) => (
                <a
                  href={`/relatorios/manutencao/${
                    (maintenance as IMaintenance).licensePlate
                  }`}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(
                      `/relatorios/manutencao/${
                        (maintenance as IMaintenance).licensePlate
                      }`
                    );
                  }}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {licensePlate as string}
                </a>
              ),
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } as any
          }
          entity="maintenance"
        />
        {isLoading && <Loading />}

        <TableFooter
          page={page || 1}
          totalItems={data.totalItems || 0}
          itemsPerPage={+(filters.itemsPerPage ?? 10)}
          goToPage={setPage}
        />
      </div>
    </Suspense>
  );
}

export default TableMaintenanceReports;
